// The peek panel: enough of a record to decide, and to change your mind, without
// leaving the sheet.
//
// This is where the time goes. Triage is a run of small decisions across many rows, and
// every full-page navigation in that run costs the list position, the scroll and the
// sort. So the panel is EDITABLE: the fields you'd reach for while triaging — stage,
// value, kind, dates, who leads it, whether it's set aside — are the same inline
// controls the detail page uses, wired to the same events. Nothing here is a read-only
// summary of something you then have to go and edit somewhere else.
//
// It is still not the detail page. It has no timeline composer and no editors for long
// prose; it shows the latest entry and hands off. Squeezing a whole record into 400px
// would recreate the detail page, badly.

import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

import { hostPath, money, relativeShort } from '../lib/format'
import {
  accentVar,
  isCommitted,
  KINDS,
  labelOf,
  ORG_STATES,
  titleField,
  type StageKind,
} from '../lib/model'
import {
  chainValue,
  commitmentsForDeal,
  commitmentsForWork,
  dealsForOrg,
  dealsForProject,
  deliveryOf,
  descriptionOf,
  entriesFor,
  fieldStr,
  lastTouch,
  linkedOfKind,
  orgsForDeal,
  projectsForDeal,
  projectsForOrg,
  renewalChain,
  stageOf,
  titleOf,
  type EntityRecord,
} from '../lib/reducer'
import { useStore } from '../lib/store'
import { EditableText, Pill, PillSelect } from './common'
import { Icon } from './Icon'
import { LinkList } from './Links'
import { Markdown } from './Markdown'
import { RefPicker } from './RefPicker'

/** Stage colour, matching the detail page: money ember, signed green, finished marine. */
function stageColor(kind: StageKind | undefined): string {
  if (kind === 'lost') return 'grey'
  if (kind === 'closed') return 'marine'
  return isCommitted(kind) ? 'green' : 'ember'
}

export function Peek({
  id,
  onClose,
  onSelect,
}: {
  id: string
  onClose: () => void
  /** re-point the panel at another record, without leaving the sheet */
  onSelect: (id: string) => void
}) {
  const { state, config, dispatch, version } = useStore()
  const navigate = useNavigate()
  void version
  const rec = state.entities[id]

  // Escape closes the panel. Registered here rather than in the layout so it only
  // exists while the panel does, and so it can't swallow Escape from an open dialog.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  if (!rec) {
    return (
      <Shell onClose={onClose}>
        <p className="meta">That record is gone.</p>
      </Shell>
    )
  }

  const def = KINDS[rec.kind]
  const upd = (data: Record<string, unknown>) =>
    void dispatch({ type: `${rec.kind}.update`, subject: id, data })
  const entries = entriesFor(state, id)
  /** Inside the panel, opening something means looking at it here. */
  const open = onSelect

  return (
    <Shell onClose={onClose}>
      <div className="eyebrow" style={{ color: `var(${accentVar(rec.kind)})` }}>
        {def.singular}
      </div>

      {/* The title is editable here too: fixing a typo shouldn't need a page load. */}
      <h2 className="text-title mt-0.5 font-semibold tracking-tight" style={{ lineHeight: 1.25 }}>
        <EditableText
          value={titleOf(state, id)}
          placeholder={`${def.singular} name`}
          onSave={(v) => upd({ [titleField(rec.kind)]: v })}
          style={{ marginLeft: -8, fontSize: 'var(--text-title)', fontWeight: 600 }}
        />
      </h2>

      <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1">
        {rec.kind === 'deal' && <DealHead deal={rec} />}
        {rec.kind === 'project' && <WorkHead work={rec} />}
        {rec.kind === 'org' && <OrgHead org={rec} />}
        {rec.kind === 'person' && <PersonHead person={rec} />}
      </div>

      <button
        type="button"
        onClick={() => {
          onClose()
          navigate(`/e/${id}`)
        }}
        className="rounded-control text-meta mt-3 flex w-full items-center justify-center gap-1.5 border py-1.5 font-medium"
        style={{ borderColor: 'var(--border)', background: 'var(--panel-2)' }}
      >
        Open full page <Icon name="ArrowRight" size={12} />
      </button>

      {rec.kind === 'deal' && <DealBody deal={rec} />}
      {rec.kind === 'project' && <WorkBody work={rec} />}
      {rec.kind === 'org' && <OrgBody org={rec} />}
      {rec.kind === 'person' && <PersonBody person={rec} />}

      <Prose text={descriptionOf(rec)} />

      {entries.length > 0 && (
        <Group title="Latest entry" note={`${entries.length} in the timeline`}>
          <div className="prose-body" style={{ fontSize: 'var(--text-body)' }}>
            <Markdown body={entries[0]!.body} />
          </div>
          <div className="meta mt-1">{entries[0]!.occurredAt}</div>
        </Group>
      )}

      <div className="mt-5 border-t pt-2" style={{ borderColor: 'var(--rule-soft)' }}>
        <span className="meta">
          {lastTouch(state, id)
            ? `touched ${relativeShort(lastTouch(state, id))}`
            : 'never touched'}
        </span>
      </div>
    </Shell>
  )

  // ── heads: the labels you change most, as controls ─────────────────────────

  function DealHead({ deal }: { deal: EntityRecord }) {
    return (
      <>
        <PillSelect
          options={config.stages.map((s) => ({
            key: s.key,
            label: s.label,
            color: stageColor(s.kind),
          }))}
          value={stageOf(deal, config.stages)}
          onPick={(stage) => {
            // Same rule as everywhere else: committing stamps the signed date once.
            const kind = config.stages.find((s) => s.key === stage)?.kind
            upd(
              isCommitted(kind) && !fieldStr(deal, 'signedAt')
                ? { stage, signedAt: new Date().toISOString().slice(0, 10) }
                : { stage },
            )
          }}
        />
        <span className="num" style={{ color: 'var(--ember)', fontSize: 'var(--text-body)' }}>
          <EditableText
            value={fieldStr(deal, 'value')}
            type="number"
            placeholder="value"
            format={(v) => money(Number(v) || 0, 'value')}
            onSave={(v) => upd({ value: Number(v.replace(/[^0-9.]/g, '')) || 0 })}
            style={{ fontFamily: 'var(--mono)', fontSize: 'var(--text-body)' }}
          />
        </span>
      </>
    )
  }

  function WorkHead({ work }: { work: EntityRecord }) {
    return (
      <PillSelect
        options={config.projectKinds}
        value={fieldStr(work, 'kind')}
        placeholder="kind"
        onPick={(kind) => upd({ kind })}
      />
    )
  }

  function OrgHead({ org }: { org: EntityRecord }) {
    // Shown, not offered. Which org is us is set once in Settings, because it
    // decides who can sign in and there is meant to be exactly one.
    const def = labelOf(ORG_STATES, fieldStr(org, 'state'))
    if (!def || def.key !== 'internal') return null
    return <Pill def={def} label={def.label} />
  }

  function PersonHead({ person }: { person: EntityRecord }) {
    return (
      <span className="text-body" style={{ color: 'var(--muted)' }}>
        <EditableText
          value={fieldStr(person, 'role')}
          placeholder="role"
          onSave={(role) => upd({ role })}
          style={{ marginLeft: -8, fontSize: 'var(--text-body)' }}
        />
      </span>
    )
  }

  // ── bodies ────────────────────────────────────────────────────────────────

  function DealBody({ deal }: { deal: EntityRecord }) {
    const stage = config.stages.find((s) => s.key === stageOf(deal, config.stages))
    const committed = isCommitted(stage?.kind) || stage?.kind === 'closed'
    const chain = renewalChain(state, deal.id)
    const orgs = orgsForDeal(state, deal.id)
    const works = projectsForDeal(state, deal.id)
    const del = deliveryOf(state, deal.id)

    return (
      <>
        <Facts>
          <Fact label="Orgs">
            {orgs.length ? (
              <span className="flex flex-wrap gap-x-1.5">
                {orgs.map((o) => (
                  <Ref key={o.id} id={o.id} onOpen={open} />
                ))}
              </span>
            ) : (
              <RefPicker
                kind="org"
                value=""
                placeholder="add a client"
                onPick={(to) => void dispatch({ type: 'link.add', subject: deal.id, data: { to } })}
              />
            )}
          </Fact>
          {committed ? (
            <Fact label="Runs">
              <span className="flex items-center gap-1">
                <DateCell
                  value={fieldStr(deal, 'signedAt')}
                  onSave={(signedAt) => upd({ signedAt })}
                />
                <span style={{ color: 'var(--faint)' }}>→</span>
                <DateCell
                  value={fieldStr(deal, 'endsAt')}
                  onSave={(endsAt) => upd({ endsAt })}
                  placeholder="open"
                />
              </span>
            </Fact>
          ) : (
            <Fact label="Expected">
              <DateCell
                value={fieldStr(deal, 'expectedClose')}
                onSave={(expectedClose) => upd({ expectedClose })}
              />
            </Fact>
          )}
          <Fact label="Links">
            <LinkList value={fieldStr(deal, 'links')} />
          </Fact>
        </Facts>

        {del.items.length > 0 && (
          <Group
            title={`Commitments · ${del.delivered} of ${del.items.length}`}
            note={del.next ? `next ${del.next.due}` : undefined}
          >
            <span className="bar mb-2 block">
              <i
                style={{
                  width: `${Math.round((del.delivered / del.items.length) * 100)}%`,
                  background: 'var(--good)',
                }}
              />
            </span>
            {commitmentsForDeal(state, deal.id).map((c) => (
              <Row
                key={c.id}
                label={c.label}
                badge={
                  <span
                    style={{
                      color: c.deliveredAt ? 'var(--good)' : 'var(--warn)',
                      fontSize: 'var(--text-label)',
                    }}
                  >
                    {c.deliveredAt ? '✓' : '○'}
                  </span>
                }
                right={c.deliveredAt || c.due || 'no date'}
              />
            ))}
          </Group>
        )}

        {chain.length > 1 && (
          <Group
            title={`Renewals · ${chain.length}`}
            note={`${money(chainValue(state, deal.id, config), '·')} booked`}
          >
            {chain.map((c) => (
              <Row
                key={c.id}
                onClick={() => open(c.id)}
                label={titleOf(state, c.id) || 'Untitled deal'}
                dim={c.id === deal.id}
                right={money(Number(c.fields.value) || 0, '·')}
              />
            ))}
          </Group>
        )}

        <Group
          title={`Projects · ${works.length}`}
          right={
            <RefPicker
              kind="project"
              value=""
              placeholder="attach"
              onPick={(project) =>
                void dispatch({ type: 'support.add', subject: deal.id, data: { project } })
              }
            />
          }
        >
          {works.length === 0 ? (
            <p className="meta py-1">None attached.</p>
          ) : (
            works.map((w) => {
              const k = labelOf(config.projectKinds, fieldStr(w, 'kind'))
              return (
                <Row
                  key={w.id}
                  onClick={() => open(w.id)}
                  label={titleOf(state, w.id)}
                  right={k?.label}
                />
              )
            })
          )}
        </Group>
      </>
    )
  }

  function WorkBody({ work }: { work: EntityRecord }) {
    const deals = dealsForProject(state, work.id)
    const promises = commitmentsForWork(state, work.id)

    return (
      <>
        <Facts>
          <Fact label="Links">
            <LinkList value={fieldStr(work, 'links') || fieldStr(work, 'url')} />
          </Fact>
          <Fact label="Led by">
            <RefPicker
              kind="person"
              value={fieldStr(work, 'owner')}
              placeholder="who leads this"
              onPick={(owner) => upd({ owner })}
              onClear={() => upd({ owner: '' })}
            />
          </Fact>
          <Fact label="Dates">
            <span className="flex items-center gap-1">
              <DateCell value={fieldStr(work, 'start')} onSave={(start) => upd({ start })} />
              <span style={{ color: 'var(--faint)' }}>→</span>
              <DateCell
                value={fieldStr(work, 'end')}
                onSave={(end) => upd({ end })}
                placeholder="open"
              />
            </span>
          </Fact>
        </Facts>

        {promises.length > 0 && (
          <Group title={`Promised in · ${promises.length}`}>
            {promises.map((c) => (
              <Row
                key={c.id}
                onClick={() => open(c.deal)}
                label={c.label}
                right={c.deliveredAt ? `delivered ${c.deliveredAt}` : c.due || 'no date'}
              />
            ))}
          </Group>
        )}

        <Group
          title={`Deals · ${deals.length}`}
          right={
            <RefPicker
              kind="deal"
              value=""
              placeholder="attach"
              onPick={(dealId) =>
                void dispatch({ type: 'support.add', subject: dealId, data: { project: work.id } })
              }
            />
          }
        >
          {deals.length === 0 ? (
            <p className="meta py-1">None attached.</p>
          ) : (
            deals.map((d) => {
              const st = config.stages.find((s) => s.key === stageOf(d, config.stages))
              return (
                <Row
                  key={d.id}
                  onClick={() => open(d.id)}
                  label={titleOf(state, d.id)}
                  badge={<Pill def={{ color: stageColor(st?.kind) }} label={st?.label ?? '·'} />}
                  right={money(Number(d.fields.value) || 0, '·')}
                />
              )
            })
          )}
        </Group>
      </>
    )
  }

  function OrgBody({ org }: { org: EntityRecord }) {
    const deals = dealsForOrg(state, org.id)
    const people = linkedOfKind(state, org.id, 'person')
    const works = projectsForOrg(state, org.id)
    const kindOf = (d: EntityRecord) =>
      config.stages.find((s) => s.key === stageOf(d, config.stages))?.kind
    const open$ = deals
      .filter((d) => kindOf(d) === 'open')
      .reduce((s, d) => s + (Number(d.fields.value) || 0), 0)
    const won$ = deals
      .filter((d) => isCommitted(kindOf(d)) || kindOf(d) === 'closed')
      .reduce((s, d) => s + (Number(d.fields.value) || 0), 0)

    return (
      <>
        <Facts>
          <Fact label="Open">
            <span className="num" style={{ color: 'var(--ember)' }}>
              {money(open$, '·')}
            </span>
          </Fact>
          <Fact label="Booked">
            <span className="num" style={{ color: 'var(--good)' }}>
              {money(won$, '·')}
            </span>
          </Fact>
          <Fact label="Where">
            <EditableText
              value={fieldStr(org, 'location')}
              placeholder="location"
              onSave={(location) => upd({ location })}
              style={{ marginLeft: -8, fontSize: 'var(--text-body)' }}
            />
          </Fact>
          <Fact label="Site">
            {fieldStr(org, 'website') ? (
              <a
                href={fieldStr(org, 'website')}
                target="_blank"
                rel="noreferrer"
                className="truncate hover:underline"
                style={{
                  color: 'var(--marine)',
                  fontFamily: 'var(--mono)',
                  fontSize: 'var(--text-label)',
                }}
              >
                {hostPath(fieldStr(org, 'website'), 28)}
              </a>
            ) : (
              <EditableText
                value=""
                type="url"
                placeholder="https://"
                onSave={(website) => upd({ website })}
                style={{ marginLeft: -8, fontSize: 'var(--text-body)' }}
              />
            )}
          </Fact>
        </Facts>

        <Group
          title={`People · ${people.length}`}
          right={
            <RefPicker
              kind="person"
              value=""
              placeholder="add"
              onPick={(to) => void dispatch({ type: 'link.add', subject: org.id, data: { to } })}
            />
          }
        >
          {people.length === 0 ? (
            <p className="meta py-1">Nobody yet.</p>
          ) : (
            people.map((p) => (
              <Row
                key={p.id}
                onClick={() => open(p.id)}
                label={titleOf(state, p.id)}
                right={fieldStr(p, 'role')}
              />
            ))
          )}
        </Group>

        {deals.length > 0 && (
          <Group title={`Deals · ${deals.length}`}>
            {deals.map((d) => {
              const st = config.stages.find((s) => s.key === stageOf(d, config.stages))
              return (
                <Row
                  key={d.id}
                  onClick={() => open(d.id)}
                  label={titleOf(state, d.id)}
                  badge={<Pill def={{ color: stageColor(st?.kind) }} label={st?.label ?? '·'} />}
                  right={money(Number(d.fields.value) || 0, '·')}
                />
              )
            })}
          </Group>
        )}

        {works.length > 0 && (
          <Group title={`Projects and artifacts · ${works.length}`} note="what their deals pay for">
            {works.map((w) => (
              <Row key={w.id} onClick={() => open(w.id)} label={titleOf(state, w.id)} />
            ))}
          </Group>
        )}
      </>
    )
  }

  function PersonBody({ person }: { person: EntityRecord }) {
    const orgs = linkedOfKind(state, person.id, 'org')
    const email = fieldStr(person, 'email')

    return (
      <>
        <Facts>
          <Fact label="Email">
            {email ? (
              <a
                href={`mailto:${email}`}
                className="truncate hover:underline"
                style={{
                  color: 'var(--marine)',
                  fontFamily: 'var(--mono)',
                  fontSize: 'var(--text-label)',
                }}
              >
                {email}
              </a>
            ) : (
              <EditableText
                value=""
                type="email"
                placeholder="name@example.com"
                onSave={(v) => upd({ email: v })}
                style={{ marginLeft: -8, fontSize: 'var(--text-body)' }}
              />
            )}
          </Fact>
          <Fact label="Phone">
            <EditableText
              value={fieldStr(person, 'phone')}
              placeholder="phone"
              onSave={(phone) => upd({ phone })}
              style={{ marginLeft: -8, fontSize: 'var(--text-body)' }}
            />
          </Fact>
        </Facts>

        <Group
          title={`Orgs · ${orgs.length}`}
          right={
            <RefPicker
              kind="org"
              value=""
              placeholder="add"
              onPick={(to) => void dispatch({ type: 'link.add', subject: person.id, data: { to } })}
            />
          }
        >
          {orgs.length === 0 ? (
            <p className="meta py-1">No orgs yet.</p>
          ) : (
            orgs.map((o) => (
              <Row key={o.id} onClick={() => open(o.id)} label={titleOf(state, o.id)} />
            ))
          )}
        </Group>
      </>
    )
  }
}

/** The panel itself: a column beside the sheet on a wide screen, a sheet over it below. */
function Shell({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <aside
      className="fixed inset-0 z-40 overflow-auto border-l px-4 pt-3 pb-16 lg:static lg:z-auto lg:w-[400px] lg:shrink-0"
      style={{ background: 'var(--panel)', borderColor: 'var(--border)' }}
    >
      <button
        type="button"
        onClick={onClose}
        className="rounded-control float-right -mr-1 flex h-7 w-7 items-center justify-center hover:bg-[var(--hover)]"
        style={{ color: 'var(--faint)' }}
        aria-label="Close"
        title="Close · Esc"
      >
        <Icon name="X" size={14} />
      </button>
      {children}
    </aside>
  )
}

/** The record's own words. */
function Prose({ text }: { text: string }) {
  if (!text.trim()) return null
  return (
    <div
      className="prose-body mt-4 border-t pt-3"
      style={{ fontSize: 'var(--text-body)', borderColor: 'var(--rule-soft)' }}
    >
      <Markdown body={text} />
    </div>
  )
}

/**
 * The facts grid. One rule keeps the panel from looking like a stack of boxes: rows are
 * separated by a hairline BETWEEN them and never above the first or below the last, so a
 * Facts block sitting under a Group can't produce the double line it used to.
 */
function Facts({ children }: { children: React.ReactNode }) {
  return <div className="mt-3">{children}</div>
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div
      className="text-body flex items-baseline gap-3 border-t py-1.5 first:border-t-0"
      style={{ borderColor: 'var(--rule-soft)' }}
    >
      <span className="eyebrow w-[68px] shrink-0">{label}</span>
      <span className="min-w-0 flex-1">{children}</span>
    </div>
  )
}

function Group({
  title,
  note,
  right,
  children,
}: {
  title: string
  note?: string | undefined
  right?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <section className="mt-4">
      <div className="mb-0.5 flex items-baseline gap-2">
        <h3 className="eyebrow">{title}</h3>
        {note && <span className="meta">{note}</span>}
        {right && <span className="ml-auto">{right}</span>}
      </div>
      {children}
    </section>
  )
}

function Row({
  label,
  badge,
  right,
  onClick,
  dim,
}: {
  label: string
  /** after the name, never before it: the name is what you're looking for */
  badge?: React.ReactNode
  right?: string | undefined
  onClick?: (() => void) | undefined
  dim?: boolean
}) {
  const inner = (
    <>
      <span className="text-body min-w-0 flex-1 truncate">{label || 'Untitled'}</span>
      {badge}
      {right && (
        <span
          className="num shrink-0"
          style={{ fontSize: 'var(--text-label)', color: 'var(--faint)' }}
        >
          {right}
        </span>
      )}
    </>
  )
  const cls = 'flex w-full items-center gap-2 border-t py-1.5 text-left first:border-t-0'
  const style = { borderColor: 'var(--rule-soft)', opacity: dim ? 0.6 : 1 }
  return onClick ? (
    <button
      type="button"
      onClick={onClick}
      className={`${cls} hover:bg-[var(--hover)]`}
      style={style}
    >
      {inner}
    </button>
  ) : (
    <div className={cls} style={style}>
      {inner}
    </div>
  )
}

/** A date that reads as a date and edits in place. */
function DateCell({
  value,
  onSave,
  placeholder = 'set date',
}: {
  value: string
  onSave: (v: string) => void
  placeholder?: string
}) {
  return (
    <EditableText
      value={value}
      type="date"
      placeholder={placeholder}
      onSave={onSave}
      style={{ marginLeft: -6, fontFamily: 'var(--mono)', fontSize: 'var(--text-meta)' }}
    />
  )
}

/** A linked record, as a chip that opens it in the panel it's already in. */
function Ref({ id, onOpen }: { id: string; onOpen: (id: string) => void }) {
  const { state } = useStore()
  return (
    <button
      type="button"
      onClick={() => onOpen(id)}
      className="text-body truncate hover:underline"
      style={{ color: 'var(--marine)' }}
    >
      {titleOf(state, id)}
    </button>
  )
}
